import { CSSProperties, ReactNode, useRef } from 'react';
import { Language } from '@/api/types';
import { LanguageSwitcher } from './LanguageSwitcher';
import './BrandHero.css';

interface BrandHeroProps {
  bgColor: string;
  inkColor: string;
  logoUrl: string;
  brandName?: string;
  welcomeTitle: ReactNode;
  welcomeMessage?: ReactNode;
  language: Language;
  availableLanguages: Language[];
  onLanguageChange: (language: Language) => void;
}

/**
 * Brand hero — purely presentational. All sizing (height, logo geometry, layer
 * opacities, shadow) is CSS, derived from the inherited `--collapse-progress`
 * var that `useCollapsingHeader` writes on the card (BrandHero.css). The only
 * JS-measured value is the logo aspect ratio (`--logo-aspect`), which positions
 * the compact brand name beside the shrunk logo.
 */
export function BrandHero({
  bgColor,
  inkColor,
  logoUrl,
  brandName,
  welcomeTitle,
  welcomeMessage,
  language,
  availableLanguages,
  onLanguageChange,
}: BrandHeroProps) {
  const heroRef = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={heroRef}
      className="brand-hero"
      style={{ background: bgColor, color: inkColor } as CSSProperties}
    >
      <div className="absolute right-4 top-4 z-20">
        <LanguageSwitcher
          value={language}
          available={availableLanguages}
          onChange={onLanguageChange}
          inkColor={inkColor}
        />
      </div>

      {/* Single shared logo: shrinks + glides from centered (expanded) to the
          top-left brand bar (compact), all via CSS calc off --collapse-progress. */}
      {logoUrl && (
        <img
          src={logoUrl}
          alt={brandName ?? 'logo'}
          onLoad={(e) => {
            const img = e.currentTarget;
            if (img.naturalHeight > 0) {
              // Set on the hero so the compact name offset (a sibling) inherits it.
              heroRef.current?.style.setProperty(
                '--logo-aspect',
                String(img.naturalWidth / img.naturalHeight)
              );
            }
          }}
          className="brand-hero__logo drop-shadow-sm"
        />
      )}

      {/* Welcome copy (expanded) — fades out on scroll. */}
      <div
        data-testid="hero-expanded"
        className="brand-hero__expanded flex flex-col items-center px-6 text-center"
      >
        <div className="text-lg font-extrabold uppercase tracking-tight drop-shadow">
          {welcomeTitle}
        </div>
        <div className="mt-1.5 max-w-[260px] text-xs font-bold uppercase tracking-wider opacity-90">
          {welcomeMessage}
        </div>
      </div>

      {/* Brand name (compact) — fades in beside the shrunk logo. */}
      <div
        data-testid="hero-compact"
        className="brand-hero__compact flex items-center"
      >
        <span className="text-lg font-bold">{brandName}</span>
      </div>
    </div>
  );
}
